import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Box, Button, Divider, Grid } from '@mui/material';
import { GridFilterModel } from '@mui/x-data-grid-pro';

import { AddressNoteApi } from '@/api';
import { ConfirmationDialog, DataGrid, RenderFormField } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { IAddressNote, IAddressNoteForm, IAddressNoteType } from '@/models';
import { useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

import { addressNoteDataGridCols, addressNoteFormFields } from '../data';
import { AddressNoteFormRoot } from '../styles';

type AddressNoteForm = IAddressNoteForm;

interface AddressNoteGridProps {
  addressId?: string;
  onNoteChange?: () => void; // New callback prop
}

const AddressNoteDataGrid: FC<AddressNoteGridProps> = ({
  addressId,
  onNoteChange,
}) => {
  const { user } = useUserStore();
  const { updateToast } = useToastStore();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [addressNoteTypes, setAddressNoteTypes] = useState<IAddressNoteType[]>(
    [],
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const methods = useForm<AddressNoteForm>({ mode: 'all' });
  const { handleSubmit, reset } = methods;

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `address-note-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return addressNoteDataGridCols;
      }

      return mergeColumnSettings(addressNoteDataGridCols, savedSettings);
    },
  );

  const handleDelete = async (id: string) => {
    try {
      await AddressNoteApi.deleteAddressNote(id);
      setRefreshKey((prev) => prev + 1);
      // Notify parent component about the change
      if (onNoteChange) onNoteChange();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onSubmit = async (values: AddressNoteForm) => {
    try {
      await AddressNoteApi.createAddressNote({
        comment: values.comment,
        type: values.type,
        address: addressId || '',
      });
      setRefreshKey((prev) => prev + 1);
      // Notify parent component about the change
      if (onNoteChange) onNoteChange();
      reset();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const addressNoteTypeItems = addressNoteTypes.map((noteType) => ({
    label: noteType.name,
    value: noteType.name,
  }));

  const fetchAddressNoteTypes = async () => {
    try {
      const res = await AddressNoteApi.getAddressNoteTypes();
      setAddressNoteTypes(res.data);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const fetchAddressNotes = useCallback(
    async (
      page: number,
      limit: number,
      filter: GridFilterModel | string,
    ): Promise<{ rows: IAddressNote[]; totalCount: number }> => {
      try {
        const filterObj =
          typeof filter === 'string' && filter
            ? JSON.parse(filter)
            : (filter as GridFilterModel) || {
                items: [],
                quickFilterValues: [],
              };

        const params = {
          page: page || 1,
          limit: limit || 100,
          filter: JSON.stringify({
            items: [
              {
                field: 'address',
                operator: 'equals',
                value: addressId,
              },
              ...(filterObj.items || []),
            ],
            logicOperator: 'and',
            quickFilterValues: filterObj.quickFilterValues || [],
            quickFilterLogicOperator: 'and',
          }),
          sort: JSON.stringify({ createdAt: -1 }),
        };

        const response = await AddressNoteApi.getPaginatedAddressNotes(params);
        const { data } = response;
        return {
          rows: data.docs,
          totalCount: data.totalDocs,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [addressId, updateToast],
  );
  const columns = useDataGridColumns({
    type: 'addressNote',
    columnSettings,
    onDelete: (id: string) => {
      setSelectedId(id);
      setShowConfirm(true);
    },
  });

  useEffect(() => {
    fetchAddressNoteTypes();
  }, [addressId]);

  return (
    <Box>
      <FormProvider {...methods}>
        <AddressNoteFormRoot onSubmit={handleSubmit(onSubmit)}>
          <Grid columnSpacing={2} flex={1} container>
            {addressNoteFormFields.map((field) => (
              <Grid
                key={field.name}
                xs={field.styles.xs}
                item
                sx={{ minHeight: 95 }}
              >
                <RenderFormField {...field} items={addressNoteTypeItems} />
              </Grid>
            ))}
          </Grid>
          <Button
            variant="contained"
            type="submit"
            color="error"
            size="large"
            fullWidth
            sx={{ mt: 2.5, ml: 2, flex: 0 }}
          >
            Add
          </Button>
        </AddressNoteFormRoot>
        <Divider />
        <Box height={350}>
          <DataGrid
            key={refreshKey}
            name="address-note"
            fetchData={fetchAddressNotes}
            columnSettings={columnSettings}
            setColumnSettings={setColumnSettings}
            columns={columns}
            hideFilter
          />
        </Box>
      </FormProvider>
      <ConfirmationDialog
        title="Delete Address Note?"
        description="Warning! You are about to delete this address note, are you sure?"
        confirmText="Delete"
        open={showConfirm}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedId('');
        }}
        onConfirm={() => {
          if (selectedId) handleDelete(selectedId);
          setShowConfirm(false);
        }}
      />
    </Box>
  );
};

export default AddressNoteDataGrid;
